import * as Sentry from '@sentry/browser';
import { getStepPayload, vriendenhypotheekAppointmentConfig } from './tealiumStepPayloadMap';

// set upon initTealiumListeners
let loggerLevelLocal;

export const TEALIUM_EVENTS = [
  'APPOINTMENT_CREATED',
  'APPOINTMENT_APP_SEGMENTATION_CHANGED',
  'INTEREST_COMPARE_CALCULATION_TRIGGERED',
  'INTEREST_COMPARE_RESULTS_SORTED',
  'INTEREST_COMPARE_ITEM_EXPANDED',
  'INTEREST_COMPARE_LOADED',
  'TOOL_STEP_CHANGED',
  'TOOL_STEP_LOADED',
  'VH_APPOINTMENT_LOADED',
  'VH_APPOINTMENT_SUCCESS',
];

/**
 * Pushes event to tealium datalayer
 *
 * @param {String} eventType. Event name
 * @param {Object} payload. Payload passed
 * @return void
 */
function pushTealiumDatalayer(eventType, payload = undefined) {
  if (loggerLevelLocal === 'info')
    console.info('[trackTealium]', { eventType, payload }); // eslint-disable-line no-console

  const event = [eventType];

  if (payload)
    event.push(payload);

  global.adv_event.push(event);

  return global.adv_event;
}

/**
 * Appointment created tracking resolver
 *
 * @return void
 */
function resolveAppointmentCreated() {
  return pushTealiumDatalayer('hypotheekadvies afspraak maken end');
}

/**
 * Appointment app loaded tracking resolver
 *
 * @return void
 */
function resolveAppointmentAppStarted() {
  return pushTealiumDatalayer('hypotheekadvies afspraak maken start');
}

/**
 * Calculation tool started & tool step loaded
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveToolStepLoaded({ payload }) {
  const stepPayload = getStepPayload(payload);

  if (!stepPayload) {
    // eslint-disable-next-line no-console
    console.log(`[trackTealium.js] Skipping... no step payload found (tool: ${payload.toolName}, step: ${payload.step}, to: ${payload.to})`);
    return;
  }

  return pushTealiumDatalayer('pageview', stepPayload);
}

/**
 * Calculation tool finished
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveInterestCompareCalculationTriggered() {
  return pushTealiumDatalayer('rentevergelijker berekenen');
}

/**
 * Calculation tool finished
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveInterestCompareResultsSorted({ payload }) {
  return pushTealiumDatalayer('rentevergelijker sort results', payload);
}

/**
 * VH Appointment loaded
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveVhAppointmentLoaded({ payload }) {
  const { defaults, steps } = vriendenhypotheekAppointmentConfig;
  const trackingPayload = {
    ...defaults,
    ...steps.loaded,
  };

  // Add form_context if provided
  if (payload.form_context)
    trackingPayload.form_context = payload.form_context;

  return pushTealiumDatalayer('pageview', trackingPayload);
}

/**
 * VH Appointment success
 *
 * @return void
 */
function resolveVhAppointmentSuccess() {
  const { defaults, steps } = vriendenhypotheekAppointmentConfig;

  return pushTealiumDatalayer('pageview', {
    ...defaults,
    ...steps.success,
  });
}

/**
 * Interest compare item expanded
 *
 * @return void
 */
function resolveInterestCompareItemExpanded() {
  return pushTealiumDatalayer('pageview', {
    page_name: 'Hypotheek:Vergelijken:Meer informatie',
    page_type: 'Vergelijken',
    compare_type: 'Hypotheek vergelijker',
    form_name : 'Hypotheek vergelijker',
    form_step: '1a',
    form_step_name: 'Meer informatie',
    form_type: 'Vergelijker',
    form_status: [],
  });
}

/**
 * Interest compare app loaded
 *
 * @return void
 */
function resolveInterestCompareLoaded() {
  return pushTealiumDatalayer('pageview', {
    page_name: 'Hypotheek:Vergelijken:Resultaten',
    page_type: 'Vergelijken',
    compare_type: 'Hypotheek vergelijker',
    form_name : 'Hypotheek vergelijker',
    form_step: '1',
    form_step_name: 'Resultaten',
    form_type: 'Vergelijker',
    form_status: ['start'],
  });
}

const eventResolvers = {
  APPOINTMENT_CREATED: resolveAppointmentCreated,
  APPOINTMENT_APP_SEGMENTATION_CHANGED: resolveAppointmentAppStarted,
  INTEREST_COMPARE_CALCULATION_TRIGGERED: resolveInterestCompareCalculationTriggered,
  INTEREST_COMPARE_RESULTS_SORTED: resolveInterestCompareResultsSorted,
  INTEREST_COMPARE_ITEM_EXPANDED: resolveInterestCompareItemExpanded,
  INTEREST_COMPARE_LOADED: resolveInterestCompareLoaded,
  TOOL_STEP_CHANGED: resolveToolStepLoaded,
  TOOL_STEP_LOADED: resolveToolStepLoaded,
  VH_APPOINTMENT_LOADED: resolveVhAppointmentLoaded,
  VH_APPOINTMENT_SUCCESS: resolveVhAppointmentSuccess,
};

/**
 * Entry function tealium tracking
 *
 * Checks if a specific resolver exists for an event and executes,
 * and otherwise throws an error
 *
 * @param {String} event. Event name
 * @param {Object} payload. Payload passed
 * @return void
 */
async function trackTealium(event, payload = {}) {
  try {
    if (!eventResolvers[event])
      throw new Error(`[trackTealium.js] No resolver found for tealium event: ${event}`);

    eventResolvers[event]({ event, payload: payload.data });

  } catch (err) {
    Sentry.captureException(err);
  }
}

export function initTealiumListeners({ $bus, loggerLevel }) {
  loggerLevelLocal = loggerLevel;
  global.adv_event = global.adv_event || [];

  TEALIUM_EVENTS.forEach((event) => {
    $bus[event].listen((payload) => {
      return trackTealium(event, payload);
    });
  });
}
